import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { KeyRound, ArrowLeft, Check } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/admin/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/admin/login", { replace: true }), 1500);
    } catch (err) { toast.error(formatError(err)); } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <div className="text-red-600 font-medium">Missing reset token</div>
          <Link to="/admin/forgot-password" className="btn-neon mt-4 inline-flex" data-testid="reset-request-new">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-reset-page">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-display font-bold tracking-tight text-lg text-ink-900">
          NIRAJ <span className="text-gold">BHAARDWAJ</span>
        </Link>
        <div className="mt-3 text-center overline">Set a new password</div>
        <div className="glass rounded-2xl p-8 mt-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
              {done ? <Check className="w-5 h-5 text-gold" /> : <KeyRound className="w-5 h-5 text-gold" />}
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-ink-900">{done ? "All set" : "New password"}</h1>
              <p className="text-xs text-ink-600">{done ? "Redirecting you to the sign-in screen…" : "Enter a new password for your admin account."}</p>
            </div>
          </div>
          {!done && (
            <form onSubmit={submit} className="space-y-4" data-testid="admin-reset-form">
              <label className="block">
                <div className="overline mb-2 !text-ink-600">New password</div>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg bg-cream-50 border border-ink-900/15 px-4 py-3 outline-none focus:border-gold text-ink-900"
                  data-testid="admin-reset-password" />
              </label>
              <label className="block">
                <div className="overline mb-2 !text-ink-600">Confirm password</div>
                <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-lg bg-cream-50 border border-ink-900/15 px-4 py-3 outline-none focus:border-gold text-ink-900"
                  data-testid="admin-reset-confirm" />
              </label>
              <button type="submit" disabled={loading} className="btn-neon w-full" data-testid="admin-reset-submit">
                {loading ? "Updating..." : "Update password"}
              </button>
            </form>
          )}
          <Link to="/admin/login" className="flex items-center justify-center gap-1.5 text-xs text-ink-600 hover:text-gold" data-testid="admin-reset-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to admin sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
